// w37 Pari — twilight fairy-tale tunnel-book wedding preview card.
//
// The full template is a twilight invitation (deep purple-blue, dawn,
// pale gold, dusty rose, lavender — butterflies, balloons, sparkles).
// The catalog card keeps that mood: a twilight paper-fold frame
// scattered with gold sparkles, an inner deep-twilight card, and a
// focal medallion holding a gold ornamental arch with a hovering
// butterfly and crescent moon above. Names in the template font;
// eyebrow / footer use the standard small-caps treatment.
import { type LayoutCtx } from "./_shared";

const FRAME =
  "linear-gradient(155deg, #2a2040 0%, #1a1530 55%, #3a2e54 100%)";

// Deterministic sparkle positions — keeps the catalog card pixel-stable.
const SPARKLES = [
  [10, 14], [24, 10], [40, 18], [62, 11], [78, 16], [92, 12],
  [6, 36], [88, 40], [12, 58], [86, 60], [16, 80], [82, 84],
];

export function PariPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  const rose = "#d4a59a";
  const goldDeep = "#b8945c";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FRAME,
        padding: "5%",
        display: "flex",
      }}
    >
      {SPARKLES.map(([x, y], i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: i % 3 === 0 ? 3 : 2,
            height: i % 3 === 0 ? 3 : 2,
            borderRadius: "50%",
            background: i % 2 ? accent : rose,
            boxShadow: `0 0 5px ${accent}`,
            opacity: 0.85,
          }}
        />
      ))}
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "6%",
          }}
        >
          TO'Y MAROSIMI
        </div>

        {/* Gold ornamental arch with crescent + hovering butterfly */}
        <div style={{ width: "60%", aspectRatio: "1 / 1", position: "relative", marginBottom: "6%" }}>
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `1px solid ${accent}55`,
              overflow: "hidden",
              background: "radial-gradient(circle at 50% 70%, #3a2e54 0%, #1a1530 100%)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <svg viewBox="0 0 100 100" width="90%" height="90%" aria-hidden>
              {/* crescent moon, top */}
              <defs>
                <mask id="pari-crescent">
                  <rect width="100" height="100" fill="black" />
                  <circle cx="74" cy="22" r="8" fill="white" />
                  <circle cx="80" cy="18" r="7" fill="black" />
                </mask>
              </defs>
              <circle cx="74" cy="22" r="8" fill={accent} mask="url(#pari-crescent)" />

              {/* sparkles */}
              {[[20, 18], [44, 12], [60, 28], [18, 38]].map(([cx, cy], i) => (
                <path key={i}
                  d={`M${cx} ${cy - 2} l0.8 1.4 1.6.2-1.2 1.1.3 1.6-1.5-.8-1.5.8.3-1.6-1.2-1.1 1.6-.2z`}
                  fill="#f8ecd9" opacity="0.85"
                />
              ))}

              {/* ornamental arch — gold paper-cut */}
              <path
                d="M50 90 L50 56 Q50 30 30 30 Q22 30 22 38 L22 90 Z"
                fill="none" stroke={accent} strokeWidth="1.6"
              />
              <path
                d="M50 90 L50 56 Q50 30 70 30 Q78 30 78 38 L78 90 Z"
                fill="none" stroke={accent} strokeWidth="1.6"
              />
              <path
                d="M22 90 L22 92 L78 92 L78 90 Z M50 56 Q40 48 30 48 Q34 38 50 40 Q66 38 70 48 Q60 48 50 56 Z"
                fill={goldDeep}
              />

              {/* hovering butterfly */}
              <g transform="translate(50, 64)">
                <ellipse cx="-4" cy="-1" rx="5" ry="3" fill={rose} opacity="0.9"
                  transform="rotate(-20 -4 -1)" />
                <ellipse cx="4" cy="-1" rx="5" ry="3" fill={rose} opacity="0.9"
                  transform="rotate(20 4 -1)" />
                <ellipse cx="-3" cy="3" rx="3.5" ry="2.4" fill={accent} opacity="0.85"
                  transform="rotate(-30 -3 3)" />
                <ellipse cx="3" cy="3" rx="3.5" ry="2.4" fill={accent} opacity="0.85"
                  transform="rotate(30 3 3)" />
                <rect x="-0.4" y="-4" width="0.8" height="8" rx="0.4" fill="#1a1530" />
              </g>
            </svg>
          </div>
        </div>

        <div style={{ fontFamily: font, fontSize: 26, lineHeight: 1.12, color: ink }}>
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "5%",
          }}
        >
          {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.26em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
